import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Play, Phone, MapPin, CalendarPlus, DollarSign } from "lucide-react";
import api, { num, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProgressRing from "@/components/worker/ProgressRing";
import QuickActionModal from "@/components/worker/QuickActionModal";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function TargetBar({ label, actual, target }) {
  const pct = target ? Math.min(100, Math.round((actual / target) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{label}</span><span className="font-mono-metric font-semibold text-[#0A1128]">{num(actual)} / {num(target)}</span></div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#1D4ED8] rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

const ACTIONS = [
  { key: "lead", label: "Add Lead", icon: UserPlus },
  { key: "call", label: "Record Call", icon: Phone },
  { key: "visit", label: "Record Visit", icon: MapPin },
  { key: "meeting", label: "Record Meeting", icon: CalendarPlus },
  { key: "sale", label: "Record Sale", icon: DollarSign },
];

export default function WorkerHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [action, setAction] = useState(null);

  const load = useCallback(() => {
    api.get("/me/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/me/earnings").then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.items.slice(0, 3))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const t = today?.targets || {};
  const a = today?.actual || {};
  const progress = t.tasks ? (a.tasks_completed / t.tasks) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-slate-500 text-sm">{greeting()},</p>
        <h1 className="font-head text-2xl font-extrabold text-[#0A1128]">{user?.name || "Worker"} 👋</h1>
        <p className="text-xs text-slate-400 mt-0.5">{today?.worker?.worker_id} · {today?.worker?.timezone}</p>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6 flex flex-col items-center shadow-sm">
        <p className="text-xs uppercase tracking-widest text-slate-400 mb-3">Today's Progress</p>
        <ProgressRing percent={progress} label="of daily tasks" sub={`${num(a.tasks_completed || 0)} / ${num(t.tasks || 0)} done`} />
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm space-y-4">
        <h2 className="font-head font-bold text-[#0A1128]">Today's Target</h2>
        <TargetBar label="Tasks" actual={a.tasks_completed} target={t.tasks} />
        <TargetBar label="Calls" actual={a.calls} target={t.calls} />
        <TargetBar label="Leads" actual={a.leads} target={t.leads} />
        <TargetBar label="Meetings" actual={a.meetings} target={t.meetings} />
        <TargetBar label="Sales" actual={a.sales} target={t.sales} />
      </div>

      <div>
        <h2 className="font-head font-bold text-[#0A1128] mb-3">Quick Actions</h2>
        <div className="grid grid-cols-3 gap-3">
          {ACTIONS.map((ac) => (
            <button key={ac.key} onClick={() => setAction(ac.key)} data-testid={`qa-btn-${ac.key}`}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white border border-slate-100 hover:border-[#1D4ED8] hover:shadow-md transition-all active:scale-95">
              <span className="h-11 w-11 rounded-xl bg-[#1D4ED8]/10 text-[#1D4ED8] grid place-items-center"><ac.icon size={20} /></span>
              <span className="text-xs font-medium text-[#0A1128] text-center leading-tight">{ac.label}</span>
            </button>
          ))}
          <button onClick={() => navigate("/app/tasks")} data-testid="qa-btn-start" className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-[#0A1128] hover:bg-[#0A1128]/90 transition-all active:scale-95">
            <span className="h-11 w-11 rounded-xl bg-[#1D4ED8] text-white grid place-items-center"><Play size={20} /></span>
            <span className="text-xs font-medium text-white text-center leading-tight">Start Task</span>
          </button>
        </div>
      </div>

      {earnings && (
        <button onClick={() => navigate("/app/performance")} className="w-full bg-gradient-to-r from-[#0A1128] to-[#1E293B] rounded-3xl p-6 text-left" data-testid="worker-earnings-card">
          <p className="text-xs uppercase tracking-widest text-slate-400">My Earnings</p>
          <div className="font-mono-metric text-3xl font-bold text-white mt-1">{money(earnings.total)}</div>
          <p className="text-xs text-[#1D4ED8] mt-1">{money(earnings.by_status?.approved || 0)} approved · {money(earnings.by_status?.paid || 0)} paid →</p>
        </button>
      )}

      {announcements.length > 0 && (
        <div>
          <h2 className="font-head font-bold text-[#0A1128] mb-3">Announcements</h2>
          <div className="space-y-2">
            {announcements.map((n) => (
              <div key={n.id} className="bg-white rounded-2xl border border-slate-100 p-4">
                <div className="font-semibold text-[#0A1128] text-sm">{n.title}</div>
                <div className="text-slate-500 text-sm mt-0.5">{n.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <QuickActionModal action={action} onClose={() => setAction(null)} onDone={load} />
    </div>
  );
}
